import type {
  CustomActivityTaxonomyId,
  CustomRoleTaxonomyId,
  CustomSkillTaxonomyId,
} from './filtering-types';

export type RicsSelections = {
  rics_k10: string[];
  rics_k50: string[];
  rics_k400: string[];
};

export type RicsK400ToK50Entry = {
  id?: string | null;
  rics_k50?: string | null;
};

type TaxonomyPrefix = 'custom_role' | 'custom_skill' | 'custom_activity';

const computeAdaptiveTaxonomyId = (
  prefix: TaxonomyPrefix,
  ricsK400List: RicsK400ToK50Entry[],
  ricsSelections: RicsSelections
): string => {
  const {
    rics_k10: ricsK10Ids,
    rics_k50: explicitK50Ids,
    rics_k400: ricsK400Ids,
  } = ricsSelections;

  const derivedK50FromK400 = ricsK400Ids
    .map((ricsK400Id) => {
      const ricsK400Item = ricsK400List?.find((ric) => ric?.id === ricsK400Id);
      return ricsK400Item?.rics_k50 ?? null;
    })
    .filter((d): d is string => d !== null);

  const combinedK50Ids = [...explicitK50Ids, ...derivedK50FromK400];

  const firstK10Id = ricsK10Ids[0];
  const allK10IdsAreIdentical = ricsK10Ids.every((id) => id === firstK10Id);

  if (combinedK50Ids.length > 0 && allK10IdsAreIdentical) {
    const firstK50Id = combinedK50Ids[0];
    if (combinedK50Ids.every((id) => id === firstK50Id)) {
      return `${prefix}_rics_k50_${firstK50Id}`;
    }
  }

  if (ricsK10Ids.length > 0 && allK10IdsAreIdentical) {
    return `${prefix}_rics_k10_${firstK10Id}`;
  }

  return `${prefix}_default`;
};

/**
 * Determine the role taxonomy value that best represents the current set of selections.
 *
 * The logic follows these steps:
 * 1. Build a combined list of K50 ids: explicitly–selected K50s plus those that map from the selected K400 ids.
 * 2. If all K50 ids in the combined list are identical, return that K50 taxonomy.
 * 3. Otherwise, check K10 selections – if all K10 ids are identical, return that K10 taxonomy.
 * 4. Fallback to the default taxonomy.
 */
export const getAdaptiveRoleTaxonomyValue = (input: {
  ricsK400List: RicsK400ToK50Entry[];
  ricsSelections: RicsSelections;
}): CustomRoleTaxonomyId =>
  computeAdaptiveTaxonomyId(
    'custom_role',
    input.ricsK400List,
    input.ricsSelections
  ) as CustomRoleTaxonomyId;

/**
 * Determine the skill taxonomy value that best represents the current set of selections.
 *
 * The logic follows these steps:
 * 1. Build a combined list of K50 ids: explicitly–selected K50s plus those that map from the selected K400 ids.
 * 2. If all K50 ids in the combined list are identical, return that K50 taxonomy.
 * 3. Otherwise, check K10 selections – if all K10 ids are identical, return that K10 taxonomy.
 * 4. Fallback to the default taxonomy.
 */
export const getAdaptiveSkillTaxonomyValue = (input: {
  ricsK400List: RicsK400ToK50Entry[];
  ricsSelections: RicsSelections;
}): CustomSkillTaxonomyId =>
  computeAdaptiveTaxonomyId(
    'custom_skill',
    input.ricsK400List,
    input.ricsSelections
  ) as CustomSkillTaxonomyId;

export const getAdaptiveActivityTaxonomyValue = (input: {
  ricsK400List: RicsK400ToK50Entry[];
  ricsSelections: RicsSelections;
}): CustomActivityTaxonomyId =>
  computeAdaptiveTaxonomyId(
    'custom_activity',
    input.ricsK400List,
    input.ricsSelections
  ) as CustomActivityTaxonomyId;

/**
 * Parse a numeric-taxonomy adaptive id back to the numeric taxonomy the APIs
 * expect — the inverse of `getAdaptiveSkillTaxonomyValue` /
 * `getAdaptiveActivityTaxonomyValue`: `<prefix>_rics_k50_N` -> N,
 * `<prefix>_rics_k10_N` -> 50 + N, default/malformed -> 0 (the Generic
 * taxonomy).
 *
 * NOTE: the role parser (`useAdaptiveRoleTaxonomy`) additionally maps an id of
 * `'0'` to its default; here `'0'` parses numerically like any other id.
 */
export const parseAdaptiveTaxonomyId = (
  taxonomyId: string,
  prefix: 'custom_skill' | 'custom_activity'
): number => {
  if (!taxonomyId || taxonomyId.includes('default')) {
    return 0;
  }
  if (taxonomyId.includes('rics_k10')) {
    const id = taxonomyId.split(`${prefix}_rics_k10_`)[1];
    const parsed = id ? Number.parseInt(id, 10) : Number.NaN;
    return Number.isFinite(parsed) ? 50 + parsed : 0;
  }
  if (taxonomyId.includes('rics_k50')) {
    const id = taxonomyId.split(`${prefix}_rics_k50_`)[1];
    const parsed = id ? Number.parseInt(id, 10) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};
